import { countMinMediaEligible, isMinMediaSatisfied } from "@instacover/helpers"
import { useLiveQuery } from "dexie-react-hooks"

import { PhotoType } from "@/utils/types"

import type { LiveQueryResult } from "../../types"
import { getPhotosByStep, type PhotoSlim } from "../photos/photos"
import { getVideosByStep, type VideoItem } from "../videos/videos"
import {
  findOrderedStepByNameAndSessionId,
  findOrderedSteps,
  isRealOrderedStep,
  type OrderedStepLocal,
  type RealOrderedStep,
} from "./orderedSteps"

export function useOrderedSteps(sessionId: string): LiveQueryResult<OrderedStepLocal[]> {
  return useLiveQuery(async () => ({ data: await findOrderedSteps(sessionId), isLoading: false }), [sessionId], {
    data: undefined,
    isLoading: true,
  })
}

export function useStepsByType(
  sessionId: string,
  stepType: RealOrderedStep["stepType"],
): LiveQueryResult<RealOrderedStep[]> {
  return useLiveQuery(
    async () => {
      const allSteps = await findOrderedSteps(sessionId)
      return {
        data: allSteps.filter(isRealOrderedStep).filter((s) => s.stepType === stepType),
        isLoading: false,
      }
    },
    [sessionId, stepType],
    { data: undefined, isLoading: true },
  )
}

// ─── StepWithMedia ─────────────────────────────────────────────────────────────

export type StepWithMedia = RealOrderedStep & {
  canSkip: boolean
  canUploadMultiple: boolean
  photos: PhotoSlim[]
  videos: VideoItem[]
  photosCount: number
  isMinMediaSatisfied: boolean
}

/** Single step with full data (photos, videos ...) */
export function useStepWithMedia(sessionId: string, stepName: PhotoType): LiveQueryResult<StepWithMedia> {
  return useLiveQuery(
    async () => {
      const rawStep = await findOrderedStepByNameAndSessionId(stepName, sessionId)
      if (!rawStep || !isRealOrderedStep(rawStep)) {
        return { data: undefined, isLoading: false }
      }
      const step = rawStep

      const canSkip = step.minMedia === 0
      const canUploadMultiple = step.maxMedia > 1 || step.maxMedia === -1

      const [photos, videos] = await Promise.all([
        getPhotosByStep(sessionId, stepName),
        getVideosByStep(sessionId, stepName),
      ])

      // Video steps carry their media in `videos`, every other step type in `photos`
      const media = step.stepType === "VehicleVideo" ? videos : photos
      const eligibleMediaCount = countMinMediaEligible(media)

      return {
        data: {
          ...step,
          canSkip,
          canUploadMultiple,
          photos,
          videos,
          photosCount: photos.length,
          isMinMediaSatisfied: isMinMediaSatisfied(step.minMedia, eligibleMediaCount),
        },
        isLoading: false,
      }
    },
    [sessionId, stepName],
    { data: undefined, isLoading: true },
  )
}
